import { Button, Col, Form, message, Modal, notification, Radio, Row, Space, Statistic, Upload } from "antd";
import { format } from "date-fns";
import moment from "moment";
import { useState } from "react";
import { useDeleteFileMutation, useUploadFileForObjectMutation, useUploadFileMutation } from "../../../redux/api/FileApi";
import { useAppSelector } from "../../../redux/Hooks";
import { AuctionResponse } from "../../auction/types";
import { usePrepayAuctionMutation } from "../../profile/redux/ProfileApi";
import { PrepayResponse } from "../../profile/types";
import { fileTypes } from "../types/types";
import ConfirmModal from "./ConfirmModal";
import FloatDatePicker from "./FloatDatePicker";

interface ModalProps {
    visible: boolean,
    toggleModal: () => void,
    productName: string,
    productId: number,
    statuteId: number,
    preCost: number,
    onSuccess: () => void,
}

const fileUploadTypes = ['.doc', '.docx', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    '.pdf', 'application/pdf', 'image/png', 'image/jpeg', 'image/jpg']

const PrepayModal = ({ visible, toggleModal, productName, productId, preCost, onSuccess, statuteId }: ModalProps) => {
    const [form] = Form.useForm();
    const userState = useAppSelector(state => state.login);
    const [deleteIds, setDeleteIds] = useState<number[]>([]);
    const [confirmVisible, setConfirmVisible] = useState(false);
    const [successVisible, setSuccessVisible] = useState(false);

    const [uploadFile] = useUploadFileMutation();
    const [prepayAuction] = usePrepayAuctionMutation();
    const [deleteFile] = useDeleteFileMutation();
    const [addFileToPrepay] = useUploadFileForObjectMutation();

    const normFile = (e: any) => {
        return e.fileList;
    };

    const onFileRemove = (file: any, type: string) => {
        return new Promise<boolean>((resolve, reject) => {
            Modal.confirm({
                className: "custom-modal",
                title: 'Xoá file đã chọn?',
                okText: "Xác nhận",
                cancelText: "Hủy bỏ",
                onOk: () => {
                    if (file.response?.attachmentId) {
                        let newDeleteIds = deleteIds;
                        newDeleteIds.push(file.response.attachmentId)
                        setDeleteIds(newDeleteIds);
                    }
                    resolve(true);

                },
                onCancel: () => {
                    resolve(false)
                }
            })
        })
    }

    const beforeUpload = (files: File[], type: string) => {
        let accepted = true;
        Array.from(files).forEach(file => {
            if (!fileUploadTypes.includes(file.type)) {
                accepted = false;
                message.error('Định dạng file không hợp lệ!', 5);
            }

            if (file.size / (1024 * 1024) > 5) {
                accepted = false;
                message.error('Dung lượng file không hợp lệ', 5);
            }
        })

        return accepted || Upload.LIST_IGNORE;
    }

    const customUpload = (options: any, type: string) => {
        options.onProgress({ percent: 0 })
        let formData = new FormData();
        formData.append('file', options.file);
        uploadFile({
            file: formData,
            objectType: fileTypes.PAYMENT,
            storageType: statuteId.toString(),
        }).unwrap()
            .then(res => {
                options.onProgress({ percent: 100 });
                options.onSuccess(res, options.file);
            }).catch(err => {
                options.onError();
            })
    }

    const onFinish = (values: any) => {
        deleteIds.forEach(item => {
            deleteFile(item)
        })

        if (userState.user.userId) {
            prepayAuction({
                amount: preCost,
                productId: productId,
                tradingTime: format(values.tradingTime, "yyyy-MM-dd'T'HH:mm:ss.SSS'+07:00'"),
                userId: userState.user.userId
            }).unwrap()
                .then(res => {
                    addFileAfterSubmit(res, values);
                })
                .catch(err => {
                    console.log(err)
                    notification.error({
                        message: err.data?.message || "Gửi đối soát thất bại."
                    });
                })

        }
    }

    const addFileAfterSubmit = (res: PrepayResponse, values: any) => {
        addFileToPrepay({
            listFileIds: values.file_upload?.length ? values.file_upload.map((item: any) => item.response.attachmentId) : [],
            objectId: res.id,
            objectType: fileTypes.PAYMENT,
        })
            .unwrap()
            .then(() => {
                toggleSuccessVisible();
                onSuccess();
            }).catch(err => {
                console.log(err)
                notification.error({
                    message: err.data?.message || "Gửi đối soát thất bại."
                });
            })
    }

    const toggleConfirmVisible = () => {
        setConfirmVisible(!confirmVisible);
    }

    const toggleSuccessVisible = () => {
        setSuccessVisible(!successVisible);
    }

    return (
        <>
            <Modal
                centered
                visible={visible}
                onCancel={toggleModal}
                className='custom-modal'
                bodyStyle={{ padding: '60px 105px' }}
                style={{ minWidth: 800 }}
                footer={false}
            >
                <div style={{ textAlign: 'center' }}>
                    <div className="font-size-30">Thanh toán phí đăng ký tham gia đấu giá</div>
                    <div className="font-size-16" style={{ marginTop: 25 }}>Tên tài sản: {productName}</div>
                </div>

                <div className='modal-infobox' style={{ marginTop: 35 }}>
                    <Row gutter={15}>
                        <Col span={12} style={{ textAlign: 'right' }}>
                            <span>Hình thức thanh toán: </span>
                        </Col>
                        <Col span={12} style={{ textAlign: 'left' }}>
                            <Radio className="custome-radio-disable" disabled checked>Chuyển khoản</Radio>
                        </Col>
                    </Row>
                    <Row gutter={15}>
                        <Col span={12} style={{ textAlign: 'right' }}>
                            <span>Người nộp tiền: </span>
                        </Col>
                        <Col span={12} style={{ textAlign: 'left' }}>
                            <span className='font-bold'>
                                {userState.user.fullName}
                            </span>
                        </Col>
                    </Row>
                    <Row gutter={15} align="middle">
                        <Col span={12} style={{ textAlign: 'right' }}>
                            <span>Số tiền cần thanh toán: </span>
                        </Col>
                        <Col
                            span={12}
                            className="font-bold"
                            style={{ textAlign: 'left', display: 'flex', alignItems: 'center', whiteSpace: 'pre-wrap' }}>
                            <Statistic
                                value={preCost}
                                groupSeparator='.'
                                decimalSeparator=","
                                precision={0}
                                valueStyle={{ fontSize: 14, fontFamily: 'Quicksand' }} /> VNĐ
                        </Col>
                    </Row>
                </div>
                <Form
                    className="custom-form prepay-form"
                    style={{ marginTop: 35, marginBottom: 45 }}
                    name="prepay"
                    form={form}
                    colon={false}
                    initialValues={{
                        startPrice: 0,
                    }}
                    onFinish={onFinish}
                    onFinishFailed={(errorInfo) => console.log(errorInfo)}
                >
                    <div style={{ marginTop: 20, display: 'flex', justifyContent: 'center' }}>
                        <Form.Item
                            name="tradingTime"
                            rules={[{
                                required: true, message: "Thời gian thanh toán là bắt buộc, vui lòng nhập đầy đủ."
                            }]}
                        >
                            <FloatDatePicker
                                label="Thời gian thanh toán"
                                placeholder="Thời gian thanh toán"
                                format="DD/MM/YYYY HH:mm:ss"
                                showTime
                                style={{ width: 360 }}
                                disabledDate={(current) => {
                                    return current && current < moment(0);
                                }}
                            />
                        </Form.Item>
                    </div>

                    <div className="font-bold font-size-16" style={{ marginTop: 16, textAlign: 'center' }}>File đính kèm:</div>
                    <div style={{ display: 'flex', justifyContent: 'center' }}>
                        <div style={{ width: 360, textAlign: 'center' }}>(Vui lòng chọn tài liệu định dạng PDF, DOC, DOCX, JPG, JPEG, PNG và dung lượng nhỏ hơn 5MB)</div>
                    </div>

                    <div style={{ overflow: 'auto', marginTop: 15 }}>
                        <Form.Item
                            rules={[{
                                required: true, message: "File đính kèm là bắt buộc, vui lòng nhập đầy đủ."
                            }]}
                            style={{ marginTop: 15 }}
                            name="file_upload"
                            valuePropName="fileList"
                            getValueFromEvent={normFile}>
                            <Upload
                                className="auction-file-upload"
                                listType="picture-card"
                                onRemove={(file) => onFileRemove(file, "file")}
                                customRequest={(options) => customUpload(options, "file")}
                                accept=".doc,.docx,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,
                        .pdf,application/pdf,image/jpg,image/jpeg,image/png"
                                multiple
                                beforeUpload={(file, fileList) => beforeUpload(fileList, "file")}
                                itemRender={(originNode, file, fileList, { download, remove }) => {
                                    return (
                                        <div className="auction-file-upload-item" title={file.name}>
                                            <div style={{ fontStyle: 'italic', marginRight: 15 }} className="text-ellipsis">{file.name}</div>
                                            <div className="font-size-16" onClick={remove}>
                                                x
                                            </div>
                                        </div>
                                    )
                                }}
                            >
                                <Button type="primary" style={{ background: 'white', border: '1px solid #DEDEDE', borderRadius: 10, color: "#0180C7" }}>
                                    CHỌN FILE
                                </Button>
                            </Upload>
                        </Form.Item>
                    </div>
                    <Row justify="center" style={{ marginTop: 30 }}>
                        <Space>
                            <Button className="button-gray" style={{ width: 160, height: 50 }} onClick={toggleModal}>ĐÓNG</Button>
                            <Button className="button-light-yellow" style={{ width: 160, height: 50 }} onClick={toggleConfirmVisible}>GỬI ĐỐI SOÁT</Button>
                        </Space>
                    </Row>
                </Form>
            </Modal>
            {confirmVisible ?
                <ConfirmModal
                    onCancel={toggleConfirmVisible}
                    onOk={() => {
                        form.submit();
                        toggleConfirmVisible();
                    }}
                    status="error"
                    title="Bạn có chắc chắn muốn gửi đối soát không?"
                    visible={confirmVisible}
                    cancelText="Đóng"
                    okText="Gửi đối soát"
                /> : <></>}
            {successVisible ?
                <ConfirmModal
                    onCancel={toggleConfirmVisible}
                    onOk={() => {
                        toggleModal();
                        toggleConfirmVisible();
                    }}
                    status="success"
                    title={<>
                        <div className="font-bold">Đã gửi đối soát chuyển khoản qua ngân hàng thành công!</div>
                        <div>Tổ chức Đấu giá sẽ kiểm tra và gửi xác nhận trong thời gian sớm nhất.</div>
                    </>}
                    visible={successVisible}
                    noCancelButton={true}
                    okText="Đồng ý"
                /> : <></>}
        </>
    )
}

export default PrepayModal;